import { Component, ViewChild, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { LoaderComponent } from 'src/app/shared/components/loader/loader.component';
import { LoaderService } from 'src/app/shared/services/loader/loader.service';
import { MatIconModule } from '@angular/material/icon';
import { HolidaysService } from 'src/app/shared/services/holidays/holidays.service';
import {
  OperativaGroup,
  Protocol,
  OperativaAssignmentDetails,
  OperativaGroupHoliday,
  OperativaGroupHolidayRequest,
  ExerciseOperativaGroupsResponse,
  OperativaWithAssignment,
  OperativaGroupOperativasResponse,
  TipologyId,
} from 'src/app/shared/interfaces/holidays.interface';
import { CommonModule, AsyncPipe } from '@angular/common';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { forkJoin, Observable, startWith, map } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatButtonModule } from '@angular/material/button';
import { finalize } from 'rxjs/operators';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { DeleteComponent } from 'src/app/shared/components/delete/delete.component';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatSelectModule } from '@angular/material/select';

// Table row interface for display
interface ProtocolTableRow {
  id: number;
  name: string;
  enabled: boolean;
  isAssigned: boolean; // Whether this protocol is assigned to the operative
}

// Day type interface
interface DayTypeRow {
  id: string;
  name: string;
  isAssigned: boolean;
}

// Day type values matching backend TYPE_DAYS_CHOICES
type DayType = 'laboral_five' | 'laboral_six' | 'natural';

// Interface for tipology assignment within an operativa
interface TipologyAssignment {
  tipologyId: TipologyId;
  tipologyDisplay: string;
  protocolControl: FormControl<Protocol | null>;
  filteredProtocols: Observable<Protocol[]>;
  selectedProtocol: Protocol | null;
  selectedDayType: DayType;
  holidaysNumber: number | null;
  personalDaysNumber: number | null;
  // Original values for change detection
  originalProtocolId: number | null;
  originalDayType: DayType;
  originalHolidaysNumber: number | null;
  originalPersonalDaysNumber: number | null;
}

// Interface for operativa form row with editable fields (now with 3 tipologies)
interface OperativaFormRow {
  id: number;
  name: string;
  active: boolean;
  tipologyAssignments: TipologyAssignment[];
}

@Component({
  selector: 'app-operational-form',
  imports: [
    CommonModule,
    AsyncPipe,
    LoaderComponent,
    RouterLink,
    MatIconModule,
    MatSlideToggleModule,
    FormsModule,
    ReactiveFormsModule,
    MatTableModule,
    MatSortModule,
    MatButtonModule,
    MatDatepickerModule,
    MatInputModule,
    MatFormFieldModule,
    MatNativeDateModule,
    MatAutocompleteModule,
    MatSelectModule,
  ],
  templateUrl: './operational-form.component.html',
  styleUrls: [
    './operational-form.component.css',
    '../../../shared/styles/layout.css',
  ],
})
export class OperationalFormComponent {
  dataSource: MatTableDataSource<ProtocolTableRow> =
    new MatTableDataSource<ProtocolTableRow>();
  dayTypesDataSource: MatTableDataSource<DayTypeRow> =
    new MatTableDataSource<DayTypeRow>();
  displayedColumns: string[] = ['protocol', 'assigned'];
  displayedDayTypeColumns: string[] = ['protocol', 'assigned'];
  private _liveAnnouncer = inject(LiveAnnouncer);

  announceSortChange(sortState: Sort) {
    if (sortState.direction) {
      this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
    } else {
      this._liveAnnouncer.announce('Sorting cleared');
    }
  }

  @ViewChild(MatSort) sort!: MatSort;

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
  }

  year?: number;

  operativaGroup: OperativaGroup | null = null;
  exerciseId: number | null = null;
  operativeId: number | null = null; // This is actually operativa_group_id
  assignmentId: number | null = null; // Store assignment ID for updates
  backLink: string = '/home/vacations/drivers';

  // Protocol table data (legacy - kept for reference)
  protocolTableData: ProtocolTableRow[] = [];
  originalAssignedProtocolId: number | null = null; // Track originally assigned protocol

  // Day type data (legacy - kept for reference)
  originalAssignedDayType: string | null = null; // Track originally assigned day type

  // Holidays number (legacy - kept for reference)
  holidaysNumber: number | null = null;
  originalHolidaysNumber: number | null = null;

  // Personal days number (legacy - kept for reference)
  personalDaysNumber: number | null = null;
  originalPersonalDaysNumber: number | null = null;

  // NEW: Operativa-level protocol assignment
  operativaFormRows: OperativaFormRow[] = [];
  protocols: Protocol[] = [];
  dayTypeOptions: { id: DayType; name: string }[] = [
    { id: 'laboral_five', name: 'Laboral (Lun-Vie, 5 días)' },
    { id: 'laboral_six', name: 'Laboral (Lun-Sáb, 6 días)' },
    { id: 'natural', name: 'Natural (7 días)' },
  ];

  // Submission state
  isSubmitting = false;

  // Mode detection
  isHolidaysMode = false;

  // Readonly mode - true when current date is past exercise start_date
  isReadonly = false;

  // Edit mode flag - when true, bypasses the checkExerciseStarted condition
  edit = true;

  // Exercise operativa group data
  exerciseOperativaGroup: ExerciseOperativaGroupsResponse | null = null;

  // Holidays management
  holidays: OperativaGroupHoliday[] = [];
  holidaysDataSource: MatTableDataSource<OperativaGroupHoliday> =
    new MatTableDataSource<OperativaGroupHoliday>();
  displayedHolidaysColumns: string[] = [
    'name',
    'start_date',
    'end_date',
    'actions',
  ];

  // New holiday form
  newHoliday = {
    name: '',
    start_date: null as Date | null,
    end_date: null as Date | null,
  };

  private _snackBar = inject(MatSnackBar);

  constructor(
    private loaderService: LoaderService,
    private route: ActivatedRoute,
    private router: Router,
    private holidaysService: HolidaysService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog
  ) {}

  get isLoading() {
    return this.loaderService.getLoader();
  }

  ngOnInit() {
    // Initialize day types with database values (these are the values sent to API)
    this.dayTypesDataSource.data = [
      {
        id: 'laboral_five',
        name: 'Laboral (Lun-Vie, 5 días)',
        isAssigned: false,
      },
      {
        id: 'laboral_six',
        name: 'Laboral (Lun-Sáb, 6 días)',
        isAssigned: false,
      },
      { id: 'natural', name: 'Natural (7 días)', isAssigned: false },
    ];

    // Check if exercise has started (readonly mode)
    this.checkExerciseStarted();

    // Detect mode based on route
    this.route.url.subscribe((segments) => {
      this.isHolidaysMode = segments.some(
        (segment) => segment.path === 'edit-holidays'
      );
    });

    this.route.params.subscribe((params) => {
      if (params['opId'] && params['id'] && params['year']) {
        this.operativeId = +params['opId'];
        this.exerciseId = +params['id'];
        this.year = +params['year'];
        this.backLink = `/home/vacations/drivers/exercise/${this.exerciseId}/${this.year}/operational/${this.operativeId}`;
        this.loaderService.showLoader();

        if (this.isHolidaysMode) {
          // Holidays mode still needs the separate call
          this.loadExerciseOperativaGroup();
          this.loadHolidaysData();
        } else {
          // Protocols mode gets group data from the new endpoint
          this.loadOperativeData();
        }
      }
    });
  }

  private checkExerciseStarted(): void {
    if (this.edit) {
      this.isReadonly = false;
      return;
    }

    const startDateStr = localStorage.getItem('exercise_start_date');
    if (startDateStr) {
      const startDate = new Date(startDateStr + 'T00:00:00');
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      this.isReadonly = today >= startDate;
    }
  }

  private loadExerciseOperativaGroup(): void {
    if (!this.operativeId || !this.exerciseId) return;

    this.holidaysService
      .getExerciseOperativaGroup(this.exerciseId, this.operativeId)
      .subscribe({
        next: (response) => {
          this.exerciseOperativaGroup = response;
        },
        error: (error) => {
          console.error('Error fetching exercise operativa group data', error);
        },
      });
  }

  private loadOperativeData(): void {
    if (!this.operativeId || !this.exerciseId) return;

    // Load protocols and operativas with their assignments in parallel
    forkJoin({
      protocols: this.holidaysService.getProtocols(false),
      operativasResponse: this.holidaysService.getOperativaGroupOperativas({
        operativa_group_id: this.operativeId,
        exercise_id: this.exerciseId,
      }),
    }).subscribe({
      next: ({ protocols, operativasResponse }) => {
        // Store protocols for autocomplete (only enabled ones)
        this.protocols = protocols.filter((p) => p.enabled && !p.is_deleted);

        // Set operativa group name from response
        this.operativaGroup = {
          id: operativasResponse.operativa_group.id,
          name: operativasResponse.operativa_group.name,
          external_id: operativasResponse.operativa_group.external_id,
        } as OperativaGroup;

        // Create form rows for each operativa
        this.operativaFormRows = operativasResponse.operativas
          .filter((op) => op.active)
          .map((operativa) => this.createOperativaFormRow(operativa));

        // console.log('Operativa form rows:', this.operativaFormRows);
        this.loaderService.hideLoader();
      },
      error: (error) => {
        this.loaderService.hideLoader();
        console.error('Error fetching protocols/operativas data', error);
        this.snackBar.open('Error al cargar los datos', 'Cerrar', {
          duration: 3000,
        });
      },
    });
  }

  private createOperativaFormRow(
    operativa: OperativaWithAssignment
  ): OperativaFormRow {
    // Default tipology definitions (in case API doesn't return all 3)
    const defaultTipologies: { id: TipologyId; display: string }[] = [
      { id: 1, display: 'FINDE FUERA' },
      { id: 2, display: 'FINDE CASA' },
      { id: 3, display: '1+1' },
    ];

    // Create tipology assignments from API data or defaults
    const tipologyAssignments: TipologyAssignment[] = defaultTipologies.map(
      (tipology) => {
        // Find the assignment for this tipology from API response
        const assignment = operativa.protocol_assignments?.find(
          (a) => a.tipology_id === tipology.id
        );

        // Find the protocol object if assigned
        const assignedProtocol = assignment?.protocol
          ? this.protocols.find((p) => p.id === assignment.protocol) || null
          : null;

        // Convert type_days to database value format
        const dayType =
          (this.convertDisplayValueToDatabaseValue(
            assignment?.type_days || null
          ) as DayType) || 'laboral_five';

        // Create form control for protocol autocomplete
        const protocolControl = new FormControl<Protocol | null>(
          assignedProtocol
        );

        // Create filtered protocols observable for autocomplete
        const filteredProtocols = protocolControl.valueChanges.pipe(
          startWith(assignedProtocol),
          map((value) => {
            const filterValue =
              typeof value === 'string' ? value : value?.name || '';
            return this.filterProtocols(filterValue);
          })
        );

        return {
          tipologyId: tipology.id,
          tipologyDisplay: assignment?.tipology_name || tipology.display,
          protocolControl,
          filteredProtocols,
          selectedProtocol: assignedProtocol,
          selectedDayType: dayType,
          holidaysNumber: assignment?.holidays_number ?? 30,
          personalDaysNumber: assignment?.personal_days_number ?? 2,
          // Store original values for change detection
          originalProtocolId: assignment?.protocol || null,
          originalDayType: dayType,
          originalHolidaysNumber: assignment?.holidays_number ?? null,
          originalPersonalDaysNumber: assignment?.personal_days_number ?? null,
        };
      }
    );

    return {
      id: operativa.id,
      name: operativa.name,
      active: operativa.active,
      tipologyAssignments,
    };
  }

  private filterProtocols(value: string): Protocol[] {
    const filterValue = value.toLowerCase();
    return this.protocols.filter((protocol) =>
      protocol.name.toLowerCase().includes(filterValue)
    );
  }

  displayProtocolFn(protocol: Protocol | null): string {
    return protocol ? protocol.name : '';
  }

  onProtocolSelected(tipologyAssignment: TipologyAssignment, event: any): void {
    tipologyAssignment.selectedProtocol = event.option.value;
  }

  private combineProtocolsData(
    protocols: Protocol[],
    assignedProtocolId: number | null
  ): void {
    // Filter only enabled and not deleted protocols
    const activeProtocols = protocols.filter((p) => p.enabled && !p.is_deleted);

    // Map protocols to table rows
    this.protocolTableData = activeProtocols.map((protocol) => ({
      id: protocol.id,
      name: protocol.name,
      enabled: protocol.enabled,
      isAssigned: protocol.id === assignedProtocolId,
    }));

    // Store original assignment for comparison when saving
    this.originalAssignedProtocolId = assignedProtocolId;

    this.dataSource.data = this.protocolTableData;
  }

  private convertDisplayValueToDatabaseValue(
    displayValue: string | null
  ): string | null {
    // API may return display values or database values
    // Convert to database values ('laboral_five', 'laboral_six', 'natural')
    if (!displayValue) return null;

    const mapping: { [key: string]: string } = {
      // New API display values
      'Laboral (Mon-Fri, 5 days)': 'laboral_five',
      'Laboral (Mon-Sat, 6 days)': 'laboral_six',
      'Natural (7 days)': 'natural',
      // Database values (pass through)
      laboral_five: 'laboral_five',
      laboral_six: 'laboral_six',
      natural: 'natural',
      // Legacy values (migrate to laboral_five)
      Laboral: 'laboral_five',
      Natural: 'natural',
      laboral: 'laboral_five',
    };

    return mapping[displayValue] || null;
  }

  private setDayTypeAssignment(assignedDayType: string | null): void {
    // Convert API display value to database value
    const dbValue = this.convertDisplayValueToDatabaseValue(assignedDayType);

    // Update the day types data source to mark the assigned day type
    this.dayTypesDataSource.data = this.dayTypesDataSource.data.map(
      (dayType) => ({
        ...dayType,
        isAssigned: dayType.id === dbValue,
      })
    );

    // Store original assignment for comparison when saving (using db value)
    this.originalAssignedDayType = dbValue;
  }

  getSelectedProtocol(): number | null {
    const selected = this.protocolTableData.find((row) => row.isAssigned);
    return selected ? selected.id : null;
  }

  getSelectedDayType(): string | null {
    const selected = this.dayTypesDataSource.data.find((row) => row.isAssigned);
    return selected ? selected.id : null;
  }

  onProtocolChange(selectedProtocol: ProtocolTableRow): void {
    // If the clicked protocol is being activated
    if (selectedProtocol.isAssigned) {
      // Deactivate all other protocols (only one can be assigned)
      this.dataSource.data.forEach((protocol) => {
        if (protocol !== selectedProtocol) {
          protocol.isAssigned = false;
        }
      });
    }
  }

  onDayTypeChange(selectedDayType: DayTypeRow): void {
    // If the clicked day type is being activated
    if (selectedDayType.isAssigned) {
      // Deactivate all other day types (only one can be assigned)
      this.dayTypesDataSource.data.forEach((dayType) => {
        if (dayType !== selectedDayType) {
          dayType.isAssigned = false;
        }
      });
    }
  }

  saveChanges(): void {
    if (this.isSubmitting) return;

    // Validate that all tipology assignments have a protocol selected
    const invalidAssignments: {
      operativaName: string;
      tipologyName: string;
    }[] = [];
    this.operativaFormRows.forEach((row) => {
      row.tipologyAssignments.forEach((tipology) => {
        if (!tipology.selectedProtocol) {
          invalidAssignments.push({
            operativaName: row.name,
            tipologyName: tipology.tipologyDisplay,
          });
        }
      });
    });

    if (invalidAssignments.length > 0) {
      const details = invalidAssignments
        .map((a) => `${a.operativaName} (${a.tipologyName})`)
        .join(', ');
      this.snackBar.open(
        `Por favor selecciona un protocolo para: ${details}`,
        'Cerrar',
        { duration: 5000 }
      );
      return;
    }

    // Collect all changed tipology assignments across all operativas
    const changedAssignments: {
      operativaId: number;
      tipology: TipologyAssignment;
    }[] = [];
    this.operativaFormRows.forEach((row) => {
      row.tipologyAssignments.forEach((tipology) => {
        const protocolChanged =
          tipology.selectedProtocol?.id !== tipology.originalProtocolId;
        const dayTypeChanged =
          tipology.selectedDayType !== tipology.originalDayType;
        const holidaysChanged =
          tipology.holidaysNumber !== tipology.originalHolidaysNumber;
        const personalDaysChanged =
          tipology.personalDaysNumber !== tipology.originalPersonalDaysNumber;

        if (
          protocolChanged ||
          dayTypeChanged ||
          holidaysChanged ||
          personalDaysChanged
        ) {
          changedAssignments.push({ operativaId: row.id, tipology });
        }
      });
    });

    if (changedAssignments.length === 0) {
      this.snackBar.open('No se han realizado cambios', 'Cerrar', {
        duration: 3000,
      });
      return;
    }

    this.loaderService.showLoader();
    this.isSubmitting = true;

    // Create upsert requests for all changed tipology assignments
    const upsertRequests = changedAssignments.map(({ operativaId, tipology }) =>
      this.holidaysService.upsertOperativaAssignment({
        operativa: operativaId,
        exercise: this.exerciseId!,
        tipology_id: tipology.tipologyId,
        protocol: tipology.selectedProtocol?.id || null,
        type_days: tipology.selectedDayType,
        holidays_number: tipology.holidaysNumber,
        personal_days_number: tipology.personalDaysNumber,
      })
    );

    // Execute all upsert operations in parallel
    forkJoin(upsertRequests)
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: (results) => {
          const created = results.filter((r) => r.created).length;
          const updated = results.filter((r) => !r.created).length;
          // console.log(`Upsert results: ${created} created, ${updated} updated`);

          this.loaderService.hideLoader();
          this.snackBar.open(
            `Operativas actualizadas correctamente (${changedAssignments.length} cambios)`,
            'Cerrar',
            { duration: 3000 }
          );
          this.router.navigate([
            '/home/vacations/drivers/exercise/' +
              this.exerciseId +
              '/' +
              this.year +
              '/operational/' +
              this.operativeId,
          ]);
        },
        error: (error) => {
          console.error('Error updating assignments:', error);
          this.loaderService.hideLoader();
          this.snackBar.open('Error al actualizar las operativas', 'Cerrar', {
            duration: 3000,
          });
        },
      });
  }

  cancel(): void {
    this.router.navigate([
      '/home/vacations/drivers/exercise/' +
        this.exerciseId +
        '/' +
        this.year +
        '/operational/' +
        this.operativeId,
    ]);
  }

  // ===== HOLIDAYS MANAGEMENT METHODS =====

  private loadHolidaysData(): void {
    if (!this.operativeId || !this.exerciseId) return;

    // Load operativa groups list and holidays in parallel
    forkJoin({
      groups: this.holidaysService.getGroups(),
      holidays: this.holidaysService.getOperativaGroupHolidays({
        operativa_group_id: this.operativeId,
        exercise_id: this.exerciseId,
      }),
    }).subscribe({
      next: ({ groups, holidays }) => {
        // Find the specific group by ID from the list
        const group = groups.find((g) => g.id === this.operativeId);
        if (!group) {
          this.loaderService.hideLoader();
          this.snackBar.open('Grupo operativo no encontrado', 'Cerrar', {
            duration: 3000,
          });
          return;
        }

        this.operativaGroup = group;
        this.holidays = holidays;
        this.holidaysDataSource.data = holidays;
        this.loaderService.hideLoader();
      },
      error: (error) => {
        this.loaderService.hideLoader();
        console.error('Error loading holidays data', error);
        this.snackBar.open('Error al cargar los datos de festivos', 'Cerrar', {
          duration: 3000,
        });
      },
    });
  }

  private formatDateToYYYYMMDD(date: Date | null): string | null {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  addHoliday(): void {
    // Validate form
    if (
      !this.newHoliday.name ||
      !this.newHoliday.start_date ||
      !this.newHoliday.end_date
    ) {
      this.snackBar.open('Por favor completa todos los campos', 'Cerrar', {
        duration: 3000,
      });
      return;
    }

    // Validate dates
    if (this.newHoliday.start_date > this.newHoliday.end_date) {
      this.snackBar.open(
        'La fecha de inicio debe ser anterior a la fecha de fin',
        'Cerrar',
        {
          duration: 3000,
        }
      );
      return;
    }

    if (!this.operativeId || !this.exerciseId) return;

    const holidayData: OperativaGroupHolidayRequest = {
      operativa_group: this.operativeId,
      exercise: this.exerciseId,
      name: this.newHoliday.name,
      start_date: this.formatDateToYYYYMMDD(this.newHoliday.start_date)!,
      end_date: this.formatDateToYYYYMMDD(this.newHoliday.end_date)!,
    };

    this.loaderService.showLoader();
    this.holidaysService.createOperativaGroupHoliday(holidayData).subscribe({
      next: (created) => {
        this.holidays.push(created);
        this.holidaysDataSource.data = this.holidays;
        // Reset form
        this.newHoliday = {
          name: '',
          start_date: null,
          end_date: null,
        };
        this.loaderService.hideLoader();
        this.snackBar.open('Festivo añadido correctamente', 'Cerrar', {
          duration: 3000,
        });
      },
      error: (error) => {
        this.loaderService.hideLoader();
        console.error('Error creating holiday:', error);
        this.snackBar.open('Error al añadir el festivo', 'Cerrar', {
          duration: 3000,
        });
      },
    });
  }

  deleteHoliday(holiday: OperativaGroupHoliday): void {
    const dialogRef = this.dialog.open(DeleteComponent, {
      data: {
        title: 'Eliminar festivo',
        message: `¿Estás seguro de que deseas eliminar el festivo "${holiday.name}"?`,
        confirmText: 'Eliminar',
        cancelText: 'Cancelar',
        handler: () =>
          this.holidaysService.deleteOperativaGroupHoliday(holiday.id),
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        // Update local data after successful deletion
        this.holidays = this.holidays.filter((h) => h.id !== holiday.id);
        this.holidaysDataSource.data = this.holidays;
      }
    });
  }

  formatDateDisplay(dateString: string): string {
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }
}
